import ClassNames from "classnames";
import {
  FunctionComponent,
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import sanitizeHtml from "sanitize-html";
import i18n from "../../i18n/index.js";
import { getActiveSite } from "../../selectors/sites.js";
import { injectScript } from "../../slices/loadStates.js";
import { useAppDispatch, useAppSelector } from "../../store/hooks.js";
import styles from "../../stylesheets/components/map-module.module.css";
import moduleBaseStyles from "../../stylesheets/components/module-base.module.css";
import {
  Accommodation,
  BaseModuleProps,
  Language,
  MapModuleSettings,
  Process,
  WindowState,
} from "../../types/index.js";
import { getRouteLink, getTranslations } from "../../utils/utils.js";
import LazyloadWrapper from "../LazyloadWrapper.js";
import UsercentricsCMPWrapper from "../UsercentricsCMPWrapper.js";

declare const process: Process;

const getAPIURL = (apiKey: string | null): URL => {
  const url = new URL("https://maps.googleapis.com/maps/api/js");
  url.searchParams.set("key", apiKey ?? process.env.GOOGLE_MAPS_API_KEY);
  return url;
};

const mapOptions: google.maps.MapOptions = {
  scrollwheel: false,
  styles: [
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "transit.station", stylers: [{ visibility: "off" }] },
  ],
};

const initMap = ({
  accommodation,
  elRef,
  zoom,
  mapRef,
  markerRef,
  infoWindowRef,
  infoWindowListenerRef,
  languageId,
}: {
  accommodation: Accommodation;
  elRef: RefObject<HTMLDivElement | null>;
  zoom: number;
  mapRef: MutableRefObject<google.maps.Map | undefined>;
  markerRef: MutableRefObject<google.maps.Marker | undefined>;
  infoWindowRef: MutableRefObject<google.maps.InfoWindow | undefined>;
  infoWindowListenerRef: MutableRefObject<
    google.maps.MapsEventListener | undefined
  >;
  languageId: Language;
}) => {
  if (!elRef.current) return;

  const position = {
    lat: accommodation.latitude,
    lng: accommodation.longitude,
  };

  const accommodationName = sanitizeHtml(accommodation.name);

  const trans = getTranslations(languageId, i18n);
  const infoWindowContent =
    accommodationName +
    `<br><a target='_blank' rel='noopener noreferrer' href='${getRouteLink({ accommodation })}'>${trans.directions}</a>`;

  mapRef.current = new google.maps.Map(elRef.current, {
    ...mapOptions,
    zoom,
    center: position,
  });
  markerRef.current = new google.maps.Marker({
    position,
    map: mapRef.current,
    title: accommodationName,
  });
  infoWindowRef.current = new google.maps.InfoWindow({
    content: infoWindowContent,
  });
  infoWindowListenerRef.current = google.maps.event.addListener(
    markerRef.current,
    "click",
    () => {
      infoWindowRef.current?.open(mapRef.current, markerRef.current);
    },
  );
  infoWindowRef.current.open(mapRef.current, markerRef.current);
};

interface Props extends BaseModuleProps<MapModuleSettings> {}

const MapModule: FunctionComponent<Props> = ({
  translatedModule,
  isPreview,
  isActive,
}) => {
  const dispatch = useAppDispatch();

  const { googleMapsApiKey: apiKey } = useAppSelector((state) =>
    getActiveSite(state.sites),
  );
  const apiURL = getAPIURL(apiKey);
  const loadStatus = useAppSelector(
    (state) => state.loadStates.scripts[apiURL.toString()],
  );
  const accommodation = useAppSelector(
    (state) => state.accommodation[translatedModule.translation.languageId],
  );

  const [isMapInitialized, setIsMapInitialized] = useState(false);
  const [isLazyloaded, setIsLazyloaded] = useState(false);
  const [consentIsGiven, setConsentIsGiven] = useState(isPreview);
  const elRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map>(undefined);
  const markerRef = useRef<google.maps.Marker>(undefined);
  const infoWindowRef = useRef<google.maps.InfoWindow>(undefined);
  const infoWindowListenerRef =
    useRef<google.maps.MapsEventListener>(undefined);

  const showTextOverlay = !isMapInitialized && isPreview;

  useEffect(() => {
    if (loadStatus === "loaded" && accommodation) {
      initMap({
        accommodation,
        elRef,
        infoWindowListenerRef,
        infoWindowRef,
        mapRef,
        markerRef,
        zoom: translatedModule.settings.zoom,
        languageId: translatedModule.translation.languageId,
      });
      setIsMapInitialized(true);
    }
  }, [loadStatus, accommodation]);

  useEffect(() => {
    mapRef.current?.setZoom(translatedModule.settings.zoom);
  }, [translatedModule.settings.zoom]);

  // On unmount
  useEffect(() => {
    return () => {
      const { google } = window as unknown as WindowState;
      if (!google || !google.maps) return;

      // There is currently no way to remove a map instace without creating a memory leak
      // https://stackoverflow.com/questions/21142483/google-maps-js-v3-detached-dom-tree-memory-leak
      infoWindowListenerRef.current &&
        google.maps.event.removeListener(infoWindowListenerRef.current);
      markerRef.current?.setMap(null);
    };
  }, []);

  const loadAPI = () => {
    dispatch(injectScript(apiURL.toString()));
  };

  useEffect(() => {
    isPreview && isActive && loadAPI();
  }, [isPreview, isActive]);

  useEffect(() => {
    !isPreview && !isActive && consentIsGiven && isLazyloaded && loadAPI();
  }, [isPreview, isActive, isLazyloaded, consentIsGiven]);

  return (
    <div
      id={translatedModule.id}
      className={ClassNames(styles.MapModule, moduleBaseStyles.Module)}
    >
      <LazyloadWrapper onLoad={setIsLazyloaded}>
        <UsercentricsCMPWrapper
          languageId={translatedModule.translation.languageId}
          serviceName="Google Maps"
          onConsentGiven={() => setConsentIsGiven(true)}
          consentIsGiven={consentIsGiven}
        >
          <div className={styles.MapModule__Container} ref={elRef} />
          {showTextOverlay && (
            <div className={styles.MapModule__TextOverlay}>
              Klicken Sie hier, um die Karte zu laden.
            </div>
          )}
        </UsercentricsCMPWrapper>
      </LazyloadWrapper>
    </div>
  );
};

export default MapModule;
